import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, AlertTriangle, TrendingDown, Home, Clock, 
  ChevronRight, BarChart3, Heart, Activity, RefreshCw,
  Filter, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { api } from '../utils/api';

const CareTeamDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cohortData, setCohortData] = useState(null);
  const [triageQueue, setTriageQueue] = useState(null);
  const [activeTriageCategory, setActiveTriageCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCareTeamData();
  }, []);

  const loadCareTeamData = async () => {
    try {
      setLoading(true);
      const [cohortRes, triageRes] = await Promise.all([
        api.getCohortAnalytics(),
        api.getTriageQueue(),
      ]);
      setCohortData(cohortRes.data);
      setTriageQueue(triageRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        navigate('/login');
      } else {
        toast.error('Failed to load CareTeam data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      T1: 'bg-red-500',
      T2: 'bg-orange-500',
      T3: 'bg-yellow-500',
      T4: 'bg-blue-500',
      T5: 'bg-green-500',
    };
    return colors[tier] || 'bg-gray-500';
  };

  const getStatusColor = (status) => {
    const colors = {
      on_track: 'bg-green-100 text-green-800 border-green-300',
      needs_attention: 'bg-orange-100 text-orange-800 border-orange-300',
      data_gap: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      on_track: 'On Track',
      needs_attention: 'Needs Attention',
      data_gap: 'Data Gap',
    };
    return labels[status] || status;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      data_gap: <Clock className="w-5 h-5 text-red-600" />,
      adherence_decline: <TrendingDown className="w-5 h-5 text-orange-600" />,
      sdoh_flagged: <Home className="w-5 h-5 text-purple-600" />,
      override_expiring: <RefreshCw className="w-5 h-5 text-blue-600" />,
    };
    return icons[category] || <AlertTriangle className="w-5 h-5" />;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      data_gap: 'Data Gap (48+ hrs)',
      adherence_decline: 'Adherence Decline',
      sdoh_flagged: 'SDOH Flagged',
      override_expiring: 'Override Expiring',
    };
    return labels[category] || category;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      data_gap: 'No data sync in 48+ hours - may need outreach',
      adherence_decline: 'Adherence dropped below 60% - intervention needed',
      sdoh_flagged: 'Social determinants of health concerns identified',
      override_expiring: 'Manual plan override expiring soon - review required',
    };
    return descriptions[category] || '';
  };

  const getAllTriagePatients = () => {
    if (!triageQueue?.categories) return [];
    
    const allPatients = [];
    Object.entries(triageQueue.categories).forEach(([category, data]) => {
      if (activeTriageCategory === 'all' || activeTriageCategory === category) {
        data.patients.forEach(patient => {
          allPatients.push({ ...patient, triageCategory: category });
        });
      }
    });
    
    // Filter by search term
    if (searchTerm) {
      return allPatients.filter(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return allPatients;
  };

  const getTotalTriageCount = () => {
    if (!triageQueue?.categories) return 0;
    return Object.values(triageQueue.categories).reduce((sum, cat) => sum + cat.count, 0);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-32"></div>)}
        </div>
        <div className="skeleton h-64"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="careteam-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            CareTeam Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Cohort management and AI-powered triage</p>
        </div>
        <Button
          onClick={loadCareTeamData}
          variant="outline"
          className="rounded-full"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Cohort Pulse Section */}
      <Card data-testid="cohort-pulse-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="w-6 h-6" style={{ color: '#8F2FDF' }} />
            <span>Cohort Pulse</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cohortData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Patients */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-purple-600" />
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {cohortData.total_patients}
                  </span>
                </div>
                <p className="text-sm text-gray-600">Total Members</p>
              </div>

              {/* Average Adherence */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100">
                <div className="flex items-center justify-between mb-2">
                  <Activity className="w-8 h-8 text-green-600" />
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {Math.round(cohortData.avg_adherence * 100)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600">Avg Adherence</p>
              </div>

              {/* AI Confidence */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <Heart className="w-8 h-8 text-blue-600" />
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {Math.round(cohortData.avg_confidence * 100)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600">Avg AI Confidence</p>
              </div>

              {/* SDOH Flagged */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-orange-50 to-red-50 border border-orange-100">
                <div className="flex items-center justify-between mb-2">
                  <Home className="w-8 h-8 text-orange-600" />
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {cohortData.sdoh_flagged_count}
                  </span>
                </div>
                <p className="text-sm text-gray-600">SDOH Flagged</p>
                <p className="text-xs text-gray-500 mt-1">{cohortData.sdoh_screened_pct}% screened</p>
              </div>
            </div>
          )}

          {/* Tier Distribution */}
          {cohortData?.tier_distribution && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Metabolic Tier Distribution</h4>
              <div className="flex items-center space-x-2">
                {Object.entries(cohortData.tier_distribution).map(([tier, count]) => (
                  <div key={tier} className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{tier}</span>
                      <span className="text-xs text-gray-500">{count}</span>
                    </div>
                    <div className="h-8 rounded-lg relative overflow-hidden bg-gray-100">
                      <div 
                        className={`absolute inset-y-0 left-0 ${getTierColor(tier)} transition-all`}
                        style={{ 
                          width: `${(count / cohortData.total_patients) * 100}%`,
                          minWidth: count > 0 ? '10%' : '0%'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                <span>T1 = Most at risk</span>
                <span>T5 = Healthiest</span>
              </div>
            </div>
          )}

          {/* Status Distribution */}
          {cohortData?.status_distribution && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Cohort Status</h4>
              <div className="flex flex-wrap gap-3">
                {Object.entries(cohortData.status_distribution).map(([status, count]) => (
                  <div 
                    key={status}
                    className={`px-4 py-2 rounded-full border ${getStatusColor(status)} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => navigate(`/patients?status=${status}`)}
                    data-testid={`status-badge-${status}`}
                  >
                    <span className="font-medium">{count}</span>
                    <span className="ml-2">{getStatusLabel(status)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Triage Queue Section */}
      <Card data-testid="triage-queue-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <span>AI Triage Queue</span>
              <Badge variant="destructive" className="ml-2">
                {getTotalTriageCount()} members
              </Badge>
            </CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48"
                  data-testid="triage-search"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={activeTriageCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTriageCategory('all')}
              className="rounded-full"
              style={activeTriageCategory === 'all' ? { background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' } : {}}
              data-testid="filter-all"
            >
              All ({getTotalTriageCount()})
            </Button>
            {triageQueue?.categories && Object.entries(triageQueue.categories).map(([category, data]) => (
              <Button
                key={category}
                variant={activeTriageCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTriageCategory(category)}
                className="rounded-full"
                style={activeTriageCategory === category ? { background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' } : {}}
                data-testid={`filter-${category}`}
              >
                {getCategoryIcon(category)}
                <span className="ml-1">{getCategoryLabel(category)} ({data.count})</span>
              </Button>
            ))}
          </div>

          {/* Category Description */}
          {activeTriageCategory !== 'all' && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>{getCategoryLabel(activeTriageCategory)}:</strong> {getCategoryDescription(activeTriageCategory)}
              </p>
            </div>
          )}

          {/* Triage Patient List */}
          <div className="space-y-3">
            {getAllTriagePatients().map((patient, index) => (
              <div
                key={`${patient.id}-${patient.triageCategory}`}
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg ${
                  patient.triageCategory === 'data_gap' ? 'border-red-200 bg-red-50' :
                  patient.triageCategory === 'adherence_decline' ? 'border-orange-200 bg-orange-50' :
                  patient.triageCategory === 'sdoh_flagged' ? 'border-purple-200 bg-purple-50' :
                  'border-blue-200 bg-blue-50'
                }`}
                onClick={() => navigate(`/patients/${patient.id}`)}
                data-testid={`triage-patient-${patient.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getCategoryIcon(patient.triageCategory)}
                      <h4 className="font-bold text-lg">
                        {patient.first_name} {patient.last_name}
                      </h4>
                      <Badge variant="outline" className="text-xs">
                        {patient.metabolic_tier}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          patient.plan_source === 'AI' ? 'bg-green-100 text-green-800' :
                          patient.plan_source === 'Override' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {patient.plan_source} Plan
                      </Badge>
                    </div>

                    <div className="flex items-center space-x-4 text-sm">
                      <div>
                        <span className="text-gray-600">DTS:</span>
                        <span className={`ml-1 font-bold ${
                          patient.digital_twin_score >= 80 ? 'text-green-600' :
                          patient.digital_twin_score >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {patient.digital_twin_score}
                        </span>
                      </div>
                      <div className="w-px h-4 bg-gray-300" />
                      <div>
                        <span className="text-gray-600">Adherence:</span>
                        <span className={`ml-1 font-bold ${
                          patient.adherence_rate >= 0.8 ? 'text-green-600' :
                          patient.adherence_rate >= 0.6 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {Math.round(patient.adherence_rate * 100)}%
                        </span>
                      </div>
                      <div className="w-px h-4 bg-gray-300" />
                      <div>
                        <span className="text-gray-600">Confidence:</span>
                        <span className="ml-1 font-medium">{Math.round(patient.confidence_score * 100)}%</span>
                      </div>
                      {patient.sdoh_flagged && (
                        <>
                          <div className="w-px h-4 bg-gray-300" />
                          <div className="flex items-center text-purple-600">
                            <Home className="w-3 h-3 mr-1" />
                            <span className="font-medium">SDOH: {patient.sdoh_domains?.join(', ')}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-purple-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/patients/${patient.id}`);
                      }}
                    >
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {getAllTriagePatients().length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No members in triage queue{activeTriageCategory !== 'all' ? ` for ${getCategoryLabel(activeTriageCategory)}` : ''}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CareTeamDashboard;
